import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import parse from 'html-react-parser';
import DisplayImage from '../display-image';
import {colors, fontSize, fontWeight, shadows, transition} from '@/styles/tokens';
import {StaticImageData} from 'next/image';

const sectionHeadingSx = {mb: 5};

const DEFAULT_HEADING_SIZE = {xs: 35, sm: 48};
const COMPACT_HEADING_SIZE = {xs: fontSize[28], sm: fontSize[32], md: fontSize[36], xl: fontSize[48]};

// On a deep-blue section the heading block flips to white; the rows stay white cards.
const darkHeadingSx = {color: colors.text.inverse} as const;
const darkSubheadingSx = {color: colors.text.inverse, '& a': {color: 'inherit', textDecoration: 'underline'}} as const;

// `columns={2}`: rows in pairs from lg. The grid's gap spaces the rows (they drop their own bottom margin), so
// the cards in a pair always end level.
const twoColumnSx = {
    display: 'grid',
    gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))'},
    gap: 2
};
const twoColumnRowSx = {mb: 0};

// `wideHeading`: the header box drops its 1024px cap from lg; the subheading keeps that reading width.
const WIDE_HEADER_MAX_WIDTH = {xs: 1024, lg: 'none'};
const wideSubheadingSx = {maxWidth: 1024, mx: 'auto'};

const rowCardSx = {
    display: 'flex',
    // Phones stack the category above its chips; a side column leaves the chips too narrow.
    flexDirection: {xs: 'column', sm: 'row'},
    alignItems: 'stretch',
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: 2,
    overflow: 'hidden',
    background: colors.background.default,
    mb: 2,
    transition: transition.base,
    '&:last-child': {mb: 0},
    '&:hover': {
        borderColor: colors.primary.main
    }
};

const categoryLabelSx = {
    minWidth: {xs: 'auto', sm: 190, md: 220},
    maxWidth: {xs: 'none', sm: 190, md: 220},
    p: {xs: 2, sm: 3},
    borderRight: {xs: 'none', sm: `1px solid ${colors.neutral.borderLight}`},
    borderBottom: {xs: `1px solid ${colors.neutral.borderLight}`, sm: 'none'},
    background: colors.background.default,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center'
};

const chipsWrapperSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    p: {xs: 2, sm: 3},
    flex: 1,
    alignContent: 'center',
    background: colors.background.tag
};

const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: 1.5,
    px: {xs: 1, sm: 1.5},
    py: 0.75,
    background: colors.background.default,
    boxShadow: shadows.sm,
    cursor: 'default',
    transition: `border-color ${transition.base}, background ${transition.base}`,
    '&:hover': {
        borderColor: colors.primary.main,
        background: colors.background.subtle
    }
};

const chipLabelSx = {
    fontSize: {xs: fontSize[12], sm: fontSize[14]},
    fontWeight: fontWeight.medium,
    color: colors.text.primary,
    lineHeight: 1.3,
    whiteSpace: 'nowrap'
};

const categoryTextSx = {
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[14], sm: fontSize[16]},
    color: colors.text.primary
};

export interface TechStackItem {
    label: string;
    icon?: string | StaticImageData;
}

export interface TechStackRow {
    category: string;
    items: TechStackItem[];
}

interface AiStackTableProps {
    heading: string;
    subheading?: string;
    contentData: TechStackRow[];
    /** Set when the section sits on a dark background. */
    dark?: boolean;
    /** Heading block alignment; `left` for pages whose sections lead with a left-aligned header. */
    align?: 'center' | 'left';
    /** Heading steps down on small screens (28 → 32 → 36 → 48) instead of 35 → 48. */
    compactHeading?: boolean;
    /** `2`: rows sit in pairs from lg (one column below, where a half row is too narrow for the chips). */
    columns?: 1 | 2;
    /** From lg, let the heading run the full container width (one line); the subheading keeps 1024px. */
    wideHeading?: boolean;
}

const AiStackTable = ({
    heading,
    subheading,
    contentData,
    dark = false,
    align = 'center',
    compactHeading = false,
    columns = 1,
    wideHeading = false
}: AiStackTableProps) => {
    return (
        <Box>
            <MUIContainer>
                <Box
                    textAlign={align}
                    maxWidth={wideHeading ? WIDE_HEADER_MAX_WIDTH : 1024}
                    margin={align === 'center' ? '0 auto' : 0}
                    sx={sectionHeadingSx}
                >
                    <Typography
                        variant="h1"
                        component="h2"
                        mb={3}
                        fontSize={compactHeading ? COMPACT_HEADING_SIZE : DEFAULT_HEADING_SIZE}
                        className="font-headers"
                        sx={dark ? darkHeadingSx : undefined}
                    >
                        {heading ? parse(heading) : ''}
                    </Typography>
                    {subheading && (
                        <Typography
                            variant="subtitle1"
                            component="p"
                            className="font-regular-text"
                            sx={[dark ? darkSubheadingSx : {}, wideHeading ? wideSubheadingSx : {}]}
                        >
                            {parse(subheading)}
                        </Typography>
                    )}
                </Box>
                <Box sx={columns === 2 ? twoColumnSx : undefined}>
                    {contentData?.map((row) => (
                        <Box key={row.category} sx={columns === 2 ? [rowCardSx, twoColumnRowSx] : rowCardSx}>
                            <Box sx={categoryLabelSx}>
                                <Typography variant="body1" className="font-headers" sx={categoryTextSx}>
                                    {row.category}
                                </Typography>
                            </Box>
                            <Box sx={chipsWrapperSx}>
                                {row.items.map((item) => (
                                    <Box key={item.label} sx={chipSx}>
                                        {item.icon && (
                                            <Box
                                                width={20}
                                                height={20}
                                                borderRadius="50%"
                                                overflow="hidden"
                                                flexShrink={0}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <DisplayImage src={item.icon} alt={item.label} width={20} height={20} />
                                            </Box>
                                        )}
                                        <Typography sx={chipLabelSx}>{item.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(AiStackTable);
